import React, { useEffect, useMemo, useState } from "react";
import DashboardShell from "../../components/DashboardShell";
import { api } from "../../lib/api";
import { useNavigate } from "react-router-dom";
import { Search, RefreshCw } from "lucide-react";

const actionStyles = {
  allow: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  deny: "bg-red-500/15 text-red-300 border-red-500/30",
  redact: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  flag: "bg-orange-500/15 text-orange-300 border-orange-500/30",
};

export default function Decisions() {
  const [rows, setRows] = useState([]);
  const [action, setAction] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = () => {
    setLoading(true);
    api.get("/v1/decisions", { params: { action, q: q || undefined, limit: 200 } })
      .then(r => setRows(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(load, [action]);

  return (
    <DashboardShell
      title="Decisions"
      subtitle="Every AI request evaluated by the Sentry Gateway."
      actions={
        <button data-testid="decisions-refresh" onClick={load} className="btn-ghost-dark text-xs">
          <RefreshCw size={12}/> Refresh
        </button>
      }
    >
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex gap-1 bg-white/5 border border-white/10 rounded-full p-1">
          {["all", "allow", "deny", "redact", "flag"].map(a => (
            <button key={a} data-testid={`filter-${a}`} onClick={() => setAction(a)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider transition ${
                action === a ? "bg-[#FF6B35] text-white" : "text-cream/70 hover:bg-white/5"
              }`}>{a}</button>
          ))}
        </div>
        <div className="flex-1 min-w-[240px] relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-cream/50" />
          <input data-testid="decisions-search" value={q} onChange={e => setQ(e.target.value)}
            onKeyDown={e => e.key === "Enter" && load()}
            placeholder="Search prompts, responses, models…"
            className="w-full bg-white/5 border border-white/10 rounded-full pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#FF6B35]/40" />
        </div>
      </div>

      <div className="rounded-xl bg-white/5 border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-cream/60">
            <tr className="text-left">
              <th className="px-4 py-3 eyebrow" style={{ fontSize: 10 }}>Action</th>
              <th className="px-4 py-3 eyebrow" style={{ fontSize: 10 }}>Prompt</th>
              <th className="px-4 py-3 eyebrow" style={{ fontSize: 10 }}>Model</th>
              <th className="px-4 py-3 eyebrow" style={{ fontSize: 10 }}>Latency</th>
              <th className="px-4 py-3 eyebrow" style={{ fontSize: 10 }}>Time</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="text-center py-8 text-cream/50">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-cream/50">No decisions yet. Try the Gateway Playground.</td></tr>}
            {rows.map(r => (
              <tr key={r.id} data-testid={`decision-row-${r.id}`}
                onClick={() => nav(`/app/decisions/${r.id}`)}
                className="border-t border-white/5 hover:bg-white/[0.04] cursor-pointer">
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full border text-[11px] font-bold uppercase ${actionStyles[r.action]}`}>{r.action}</span>
                </td>
                <td className="px-4 py-3 max-w-[380px] truncate text-cream/85">{r.prompt}</td>
                <td className="px-4 py-3 text-cream/70 font-mono text-xs">{r.provider}/{r.model}</td>
                <td className="px-4 py-3 text-cream/70 font-mono text-xs">{r.latency_ms?.toFixed(2)} ms</td>
                <td className="px-4 py-3 text-cream/50 text-xs">{new Date(r.timestamp).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashboardShell>
  );
}
